import { Injectable } from '@angular/core';
import { CompanyDomainModel } from './company-domain-model';
import { BaseDomainModel } from '../models/base/base-domain-model';
import { BehaviorSubject, combineLatest, interval, Observable, of } from 'rxjs';
import {
  concatMap,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  takeUntil,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import { DistinctUtils } from '../utils/distinct-utils';
import { SyncDataJob } from '../models/automation/sync-data-job';
import { SyncRepository } from '../repositories/sync/sync-repository';
import { SyncJobStatus } from '../utils/sync-job-status-type';
import { DateUtils } from '../utils/date-utils';
import { WebSocketService } from '../services/web-socket.service';
import { WebSocketMessageContextEvent } from '../models/web-sockets/contexts/web-socket-message-context-event';
import { WebSocketSyncJobUpdate } from '../models/web-sockets/messages/receive/web-socket-sync-job-update';
import { exists } from '../functions/exists';

// Provided by Logged In Scope
@Injectable()
export class SyncDomainModel extends BaseDomainModel {
  constructor(
    private companyDomainModel: CompanyDomainModel,
    private syncRepository: SyncRepository,
    private webSocketService: WebSocketService
  ) {
    super();
  }

  // Sync Job polling
  public readonly POLLING_TIME = 10 * 1000; // 10 seconds
  public pollingTimer$ = interval(this.POLLING_TIME);
  private _lastPolledTime = new BehaviorSubject<number>(0);
  public lastPolledTime$ = this._lastPolledTime as Observable<number>;

  public companyId$ = this.companyDomainModel.selectedCompanyId$;

  private _companySyncJobs = new BehaviorSubject<SyncDataJob[]>(null);
  public companySyncJobs$ = combineLatest([this._companySyncJobs, this.companyDomainModel.selectedCompany$]).pipe(
    map(([jobs, currentCompany]) => {
      jobs?.forEach(job => job?.autoAddPromotionsToSyncTypesIfNeeded(currentCompany.provider));
      return jobs;
    }),
    distinctUntilChanged(DistinctUtils.distinctUniquelyIdentifiableArray),
    shareReplay({ bufferSize: 1, refCount: true }),
    debounceTime(100) // delay pipe to not spam table. Spamming the table breaks it
  );

  /* ---------------------------- Local Threads of Execution  ---------------------------- */

  private getCompanySyncJobs = this.companyId$
    .notNull()
    .pipe(distinctUntilChanged())
    .subscribeWhileAlive({
      owner: this,
      next: companyId => this.fetchCompanySyncJobs(companyId)
    });

  // Admin connections receive sync job updates for every company, so only the selected company's jobs are kept.
  private listenForSyncJobUpdates = this.webSocketService
    .getRelevantMessage$<WebSocketSyncJobUpdate>(WebSocketMessageContextEvent.SyncJobUpdate)
    .pipe(
      map(message => message?.payload),
      withLatestFrom(this.companyId$),
      filter(([job, companyId]) => exists(job) && job?.companyId === companyId)
    )
    .subscribeWhileAlive({
      owner: this,
      next: ([job]) => this.handleSyncJobUpdate(job)
    });

  private fetchCompanySyncJobs(companyId: number) {
    // Clear the previous company's jobs so websocket updates for the new company are dropped until its fetch lands
    this._companySyncJobs.next(null);
    this.syncRepository.getSyncJobs$(companyId).subscribe(syncJobs => this._companySyncJobs.next(syncJobs));
  }

  public pollingInputJobs$ = this.companySyncJobs$.pipe(
    map(jobs => {
      return jobs?.filter(j => {
        // Continue to poll for jobs that are queued or processing
        const queued = SyncJobStatus[j.jobStatus] === SyncJobStatus.SyncJobStatus_Queued;
        const processing = SyncJobStatus[j.jobStatus] === SyncJobStatus.SyncJobStatus_Processing;
        const tenMinutesIntoTheFuture = j.dateCreated + DateUtils.secondsInOneMinute() * 10;
        const continuePolling = tenMinutesIntoTheFuture > DateUtils.currentTimestampInSeconds();
        return (queued || processing) && continuePolling;
      });
    })
  );

  private poller = combineLatest([this.companyId$, this.pollingInputJobs$, this.pollingTimer$, this.lastPolledTime$])
    .pipe(
      concatMap(([companyId, jobs, _, lastPolledTime]) => {
        if (jobs?.length > 0 && Date.now() - lastPolledTime > this.POLLING_TIME) {
          this._lastPolledTime.next(Date.now());
          return this.getUpdatedSyncJobStatus$(jobs, companyId);
        } else {
          return of([]);
        }
      }),
      takeUntil(this.onDestroy)
    )
    .subscribe();

  private getUpdatedSyncJobStatus$(jobs: SyncDataJob[], companyId: number): Observable<SyncDataJob[]> {
    return this.syncRepository
      .getSyncJobs$(companyId, jobs.map(j => j.id).unique())
      .pipe(tap(updatedJobs => updatedJobs.forEach(job => this.handleSyncJobUpdate(job))));
  }

  private handleSyncJobUpdate(job: SyncDataJob) {
    if (job?.jobStatus === SyncJobStatus.SyncJobStatus_Success) {
      this.companyDomainModel.refreshCompany(job.companyId);
    }
    this.updateCurrentSyncJobs(job);
  }

  private updateCurrentSyncJobs(job: SyncDataJob) {
    if (!job) return;
    this._companySyncJobs.once(jobs => {
      // The initial fetch is still in flight and will include this job.
      if (!jobs) return;
      const updatedJobs = jobs.shallowCopy();
      const existingIndex = updatedJobs.findIndex(j => j.id === job.id);
      if (existingIndex > -1) {
        updatedJobs.splice(existingIndex, 1);
      }
      updatedJobs.push(job);
      this._companySyncJobs.next(updatedJobs);
    });
  }

  public createSyncJob$(job: SyncDataJob): Observable<SyncDataJob> {
    return this.syncRepository.createSyncJob$(job).pipe(tap(newJob => this.updateCurrentSyncJobs(newJob)));
  }
}
